import { preventDefault } from '@/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  useImperativeHandle,
  useState,
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import type {
  SubmitErrorHandler,
  SubmitHandler,
  UseFormReturn,
} from 'react-hook-form'
import { Button } from './ui'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog'
import { Loader } from '@ur-solutions/react-components'

interface NavButton {
  show: boolean
  text: string
  disabled?: boolean
  onClick: MouseEventHandler<HTMLButtonElement>
}

export interface FormDialogRef {
  open(): void
  close(): void
}

interface Props<TValues extends Record<string, any>> extends PropsWithChildren {
  ref?: React.Ref<FormDialogRef>

  form: UseFormReturn<TValues>

  trigger: ReactNode
  header: string
  description?: string

  noCloseOnEscape?: boolean

  noCloseButton?: boolean
  noCancelButton?: boolean
  noConfirmButton?: boolean

  cancelText?: string
  confirmText?: string

  prevButton?: NavButton
  nextButton?: NavButton

  onClose?(): boolean | void
  onOpen?(): boolean | void
  onOpenChange?(open: boolean): boolean | void
  onSubmit: SubmitHandler<TValues>
  onSubmitInvalid?: SubmitErrorHandler<TValues>
}

export function FormDialog<TValues extends Record<string, any>>({
  children,
  ref,

  form,

  trigger,
  header,
  description,

  noCloseOnEscape = false,

  noCloseButton = false,
  noCancelButton = false,
  noConfirmButton = false,

  cancelText,
  confirmText,

  prevButton,
  nextButton,

  onClose,
  onOpen,
  onOpenChange,
  onSubmit,
  onSubmitInvalid,
}: Props<TValues>) {
  const translations = useTranslate({
    cancel: 'common.cancel',
    confirm: 'common.confirm',
  })

  const [open, setOpen] = useState(false)

  const hasFooter = !noCancelButton || !noConfirmButton

  function handleOpenChange(open: boolean) {
    let proceed = true
    if (!open && !!onClose) proceed = onClose() !== false
    else if (open && !!onOpen) proceed = onOpen() !== false

    if (!!onOpenChange) proceed = onOpenChange(open) !== false

    if (proceed) setOpen(open)
  }

  useImperativeHandle(ref, () => ({
    open: () => handleOpenChange(true),
    close: () => handleOpenChange(false),
  }))

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>

      <DialogContent
        showCloseButton={!noCloseButton}
        onEscapeKeyDown={preventDefault(noCloseOnEscape)}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {header}

            {form.formState.isSubmitting && <Loader.Spinner size={16} />}
          </DialogTitle>

          {!!description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription className="sr-only">{header}</DialogDescription>
          )}
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit, onSubmitInvalid)}>
          {children}

          {hasFooter && (
            <DialogFooter className="mt-6">
              {!noCancelButton && (
                <DialogClose asChild>
                  <Button type="button" variant="outline" size="lg">
                    {cancelText ?? translations.cancel}
                  </Button>
                </DialogClose>
              )}

              {!!prevButton?.show && (
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  disabled={prevButton.disabled}
                  onClick={prevButton.onClick}
                >
                  {prevButton.text}
                </Button>
              )}

              {!!nextButton?.show && (
                <Button
                  type="button"
                  size="lg"
                  disabled={nextButton.disabled}
                  onClick={nextButton.onClick}
                >
                  {nextButton.text}
                </Button>
              )}

              {!noConfirmButton && !nextButton?.show && (
                <Button
                  type="submit"
                  size="lg"
                  disabled={
                    !form.formState.isValid || form.formState.isSubmitting
                  }
                >
                  {confirmText ?? translations.confirm}
                </Button>
              )}
            </DialogFooter>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}
